/**
 * Type-specific document preview mocks for the HERO Dokumente detail view.
 */
import { ld } from "@/lib/theme/light-dark";
import { Image as ImageIcon, Minus, Plus } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";
import type { DocPreviewKind } from "./module-dokumente-data";
import { PreviewWordMock } from "./module-dokumente-preview-word";

const CANVAS: CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: ld("#F0F1F4", "#2b2e32"),
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const A4_PAGE: CSSProperties = {
  height: "100%",
  aspectRatio: "210 / 297",
  maxWidth: "100%",
  background: ld("#fff", "#181b1f"),
  border: `1px solid ${ld("#E6E8EB", "#33373b")}`,
  boxShadow: `0 8px 24px -12px ${ld("rgba(11,15,25,0.25)", "rgba(0,0,0,0.425)")}`,
  padding: "28px 26px",
  boxSizing: "border-box",
  overflow: "hidden",
};

function ZoomBar(): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 6,
        padding: "8px 14px",
        fontSize: 11,
        color: H.inkDim,
        flexShrink: 0,
      }}
    >
      <Minus width={14} height={14} strokeWidth={1.8} />
      <span style={{ fontWeight: 500, minWidth: 36, textAlign: "center" }}>
        100%
      </span>
      <Plus width={14} height={14} strokeWidth={1.8} />
    </div>
  );
}

function PreviewPdfMock(): ReactNode {
  const t = useTranslations("marketing.moduleDokumentePreviews");
  const row = (label: string, amount: string): ReactNode => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "5px 0",
        borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
        fontSize: 10,
        color: H.inkSoft,
      }}
    >
      <span>{label}</span>
      <span style={{ fontWeight: 500, color: H.ink }}>{amount}</span>
    </div>
  );
  return (
    <div style={CANVAS}>
      <ZoomBar />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          padding: "0 20px 16px",
          boxSizing: "border-box",
        }}
      >
        <div style={A4_PAGE}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: H.ink }}>
                {t("hausmeisterSchmidt")}
              </div>
              <div style={{ fontSize: 9, color: H.inkDim, marginTop: 3 }}>
                {t("dienstleistungenAhornweg5")}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontFamily: H.label,
                  fontSize: 9,
                  letterSpacing: "0.06em",
                  color: H.inkGhost,
                }}
              >
                {t("rechnung")}
              </div>
              <div style={{ fontSize: 9, color: H.inkDim, marginTop: 3 }}>
                {t("nr20251147")}
              </div>
            </div>
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: H.ink,
              marginBottom: 4,
            }}
          >
            {t("treppenhausreinigungNovember2025")}
          </div>
          <div style={{ fontSize: 9.5, color: H.inkDim, marginBottom: 14 }}>
            {t("liegenschaftAhornweg5Ausgestellt")}
          </div>
          {row(t("treppenhausreinigungNovember"), "380,00 €")}
          {row(t("reinigungsmittel"), "25,00 €")}
          {row(t("anfahrt"), "15,00 €")}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 12,
              paddingTop: 10,
              borderTop: `1px solid ${ld("#E3E6EA", "#35383c")}`,
              fontSize: 10,
              color: H.inkSoft,
            }}
          >
            <span>{t("netto")}</span>
            <span>{t("k42000")}</span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "4px 0",
              fontSize: 10,
              color: H.inkSoft,
            }}
          >
            <span>{t("mwst19")}</span>
            <span>{t("k7980")}</span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 6,
              paddingTop: 8,
              borderTop: `1px solid ${ld("#C3C8CF", "#505357")}`,
              fontSize: 11,
              fontWeight: 700,
              color: H.ink,
            }}
          >
            <span>{t("brutto")}</span>
            <span>{t("k49980")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewSpreadsheetMock(): ReactNode {
  const t = useTranslations("marketing.moduleDokumentePreviews");
  const cols = [t("mieter"), t("einheit"), t("offen"), t("faellig")];
  const rows = [
    ["Petra Sommer", "WE 3", "420,00 €", "01.07."],
    ["Max Berger", "WE 7", "180,50 €", "15.06."],
    // i18n-exempt: sample person name
    ["Lisa Kraft", "WE 12", "95,00 €", "01.07."],
    [t("thomasWeiss"), "WE 1", "0,00 €", "—"],
  ];
  return (
    <div style={CANVAS}>
      <ZoomBar />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          padding: "0 16px 16px",
        }}
      >
        <div
          style={{
            width: "88%",
            alignSelf: "center",
            background: ld("#fff", "#181b1f"),
            border: `1px solid ${ld("#E6E8EB", "#33373b")}`,
            borderRadius: 4,
            boxShadow: `0 8px 24px -12px ${ld("rgba(11,15,25,0.2)", "rgba(0,0,0,0.35)")}`,
            overflow: "hidden",
            fontSize: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              borderBottom: `1px solid ${ld("#E3E6EA", "#35383c")}`,
              background: ld("#E8F5EF", "#26312c"),
            }}
          >
            {cols.map((col, i) => (
              <div
                key={col}
                style={{
                  flex: i === 0 ? 2 : 1,
                  padding: "7px 8px",
                  fontWeight: 600,
                  color: H.accent,
                  borderRight:
                    i < cols.length - 1 ? `1px solid ${ld("#D8EBE4", "#2b3c36")}` : undefined,
                }}
              >
                {col}
              </div>
            ))}
          </div>
          {rows.map((row, ri) => (
            <div
              key={row[0]}
              style={{
                display: "flex",
                borderBottom:
                  ri < rows.length - 1 ? `1px solid ${ld("#F1F2F4", "#2b2e32")}` : undefined,
              }}
            >
              {row.map((cell, ci) => (
                <div
                  key={`${ri}-${ci}`}
                  style={{
                    flex: ci === 0 ? 2 : 1,
                    padding: "6px 8px",
                    color: H.inkSoft,
                    borderRight:
                      ci < row.length - 1 ? `1px solid ${ld("#F5F6F8", "#25282c")}` : undefined,
                  }}
                >
                  {cell}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewImageMock(props: { readonly label: string }): ReactNode {
  return (
    <div style={CANVAS}>
      <ZoomBar />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div
          style={{
            width: "70%",
            maxWidth: 320,
            aspectRatio: "4 / 3",
            background: `linear-gradient(145deg, ${ld("#E8E4DF", "#36393d")} 0%, ${ld("#D4CFC8", "#484b50")} 100%)`,
            borderRadius: 6,
            boxShadow: `0 12px 32px -16px ${ld("rgba(11,15,25,0.35)", "rgba(0,0,0,0.575)")}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            color: H.inkDim,
            padding: "0 12px",
            boxSizing: "border-box",
          }}
        >
          <ImageIcon width={36} height={36} strokeWidth={1.4} />
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              textAlign: "center",
              lineHeight: 1.35,
            }}
          >
            {props.label}
          </span>
        </div>
      </div>
    </div>
  );
}

export function DokumentePreview(props: {
  readonly kind: DocPreviewKind;
  readonly imageLabel?: string;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumentePreviews");
  switch (props.kind) {
    case "pdf":
      return <PreviewPdfMock />;
    case "xlsx":
      return <PreviewSpreadsheetMock />;
    case "docx":
      return <PreviewWordMock />;
    case "jpg":
      return <PreviewImageMock label={props.imageLabel ?? t("bild")} />;
  }
}
